import { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

interface Props {
  /** The snippet, exactly as it should end up in somebody's editor. */
  code: string
  /** What this snippet is, in the reader's terms. */
  title: string
  /** One line under the title, where the snippet needs a caveat. */
  caption?: string
}

/**
 * A copyable code sample inside a right-to-left page.
 *
 * `dir="ltr"` on the block itself, not on the page: source code in an RTL
 * container has its punctuation reordered — a trailing comma jumps to the front
 * of the line — and a customer who copies it gets something that no longer
 * parses. The heading above it stays in the page's own direction.
 *
 * Copy is a button rather than "select the text", because these samples are
 * several lines long and a partial selection is a support ticket.
 */
export function CodeBlock({ code, title, caption }: Props) {
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard access can be refused by the browser. The sample is still on
      // screen and selectable, so failing quietly leaves nobody worse off.
    }
  }

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{
          alignItems: { sm: 'center' },
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          bgcolor: 'action.hover',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2">{title}</Typography>
          {caption ? (
            <Typography variant="caption" color="text.secondary">
              {caption}
            </Typography>
          ) : null}
        </Box>
        <Button size="small" onClick={() => void copy()} sx={{ flexShrink: 0 }}>
          {copied ? 'کپی شد' : 'کپی کردن'}
        </Button>
      </Stack>
      <Box
        component="pre"
        dir="ltr"
        // Inline, not `sx`: the app's emotion cache runs stylis-plugin-rtl,
        // which rewrites `direction: ltr` to `rtl` and `text-align: left` to
        // `right` — flipping the very declarations that keep the snippet
        // readable. Inline styles are not processed by stylis, so they hold.
        style={{ direction: 'ltr', textAlign: 'left' }}
        sx={{
          // Isolation, so the block cannot be reordered by the Persian
          // paragraph around it. `unicode-bidi` is one of the properties the
          // RTL plugin leaves alone, so it is safe in `sx`.
          unicodeBidi: 'isolate',
          m: 0,
          p: 2,
          overflowX: 'auto',
          fontFamily: 'monospace',
          fontSize: '0.8125rem',
          lineHeight: 1.7,
          bgcolor: 'background.paper',
          color: 'text.primary',
        }}
      >
        {code}
      </Box>
    </Paper>
  )
}
